import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronRight, Pencil, Plus, Trash2, X } from "lucide-react";
import { COMMON_LANGUAGES, LANGUAGE_LEVELS } from "../../../constants";
import type { Language } from "../../../types";

const MAX_PROFILE_LANGUAGES = 10;

const LANGUAGE_LEVEL_DESCRIPTIONS: Record<
  "fluent" | "conversational" | "basic",
  string
> = {
  fluent: "Comfortable with everyday and nuanced conversation.",
  conversational: "Comfortable holding an everyday conversation.",
  basic: "Can communicate simple needs and ideas.",
};

type ModernLanguageLevel = keyof typeof LANGUAGE_LEVEL_DESCRIPTIONS;
type EditorTarget = number | "new";

function modernLanguageLevel(level: Language["level"]): ModernLanguageLevel {
  if (level === "fluent") return "fluent";
  if (
    level === "conversational"
    || level === "spoken_written"
    || level === "spoken_only"
  ) {
    return "conversational";
  }
  return "basic";
}

export function normalizeProfileLanguageName(value: string): string {
  return value.normalize("NFKC").trim().replace(/\s+/g, " ");
}

export function languageEditorError(
  languages: readonly Language[],
  draftName: string,
  target: EditorTarget,
): string | null {
  const normalized = normalizeProfileLanguageName(draftName);
  if (!normalized) return "Enter a language.";
  const key = normalized.toLocaleLowerCase("en-US");
  const duplicate = languages.some((language, index) => (
    index !== target
    && normalizeProfileLanguageName(language.language).toLocaleLowerCase("en-US") === key
  ));
  return duplicate ? "That language is already on your profile." : null;
}

export function ProfileLanguagesField({
  languages,
  onChange,
  error,
}: {
  languages: Language[];
  onChange: (languages: Language[]) => void;
  error?: string;
}) {
  const listId = useId();
  const titleId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);
  const [draft, setDraft] = useState<Language>({
    language: "",
    level: "conversational",
  });
  const [draftError, setDraftError] = useState<string | null>(null);

  const closeEditor = () => {
    const returnFocus = returnFocusRef.current;
    setEditorTarget(null);
    setDraftError(null);
    requestAnimationFrame(() => returnFocus?.focus());
  };

  useEffect(() => {
    if (editorTarget === null) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeEditor();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [editorTarget]);

  const openEditor = (target: EditorTarget, trigger: HTMLElement) => {
    const language = target === "new" ? null : languages[target];
    returnFocusRef.current = trigger;
    setDraft({
      language: language?.language ?? "",
      level: modernLanguageLevel(language?.level ?? "conversational"),
    });
    setDraftError(null);
    setEditorTarget(target);
  };

  const saveLanguage = () => {
    if (editorTarget === null) return;
    const validationError = languageEditorError(
      languages,
      draft.language,
      editorTarget,
    );
    if (validationError) {
      setDraftError(validationError);
      inputRef.current?.focus();
      return;
    }
    const language: Language = {
      language: normalizeProfileLanguageName(draft.language),
      level: modernLanguageLevel(draft.level),
    };
    onChange(editorTarget === "new"
      ? [...languages, language]
      : languages.map((current, index) => (
          index === editorTarget ? language : current
        )));
    closeEditor();
  };

  const removeLanguage = (index: number) => {
    if (languages.length <= 1) return;
    onChange(languages.filter((_, rowIndex) => rowIndex !== index));
  };

  return (
    <div className={`profile-languages-field${error ? " profile-languages-field--invalid" : ""}`}>
      <div className="profile-language-rows">
        {languages.map((language, index) => {
          const level = modernLanguageLevel(language.level);
          const levelLabel = LANGUAGE_LEVELS.find((option) => option.value === level)?.label ?? "Conversational";
          return (
            <div className="profile-language-row" key={index}>
              <button
                className="profile-language-edit-card"
                type="button"
                onClick={(event) => openEditor(index, event.currentTarget)}
                aria-label={`Edit ${language.language || `language ${index + 1}`}`}
              >
                <span className="profile-language-card-copy">
                  <span>Language {index + 1}</span>
                  <strong>{language.language || "Language needed"}</strong>
                  <small>Proficiency · {levelLabel}</small>
                </span>
                <span className="profile-language-card-action" aria-hidden="true">
                  <Pencil size={16} />
                  Edit
                </span>
                <ChevronRight size={18} aria-hidden="true" />
              </button>
              <button
                className="profile-language-remove"
                type="button"
                onClick={() => removeLanguage(index)}
                disabled={languages.length <= 1}
                aria-label={`Remove ${language.language || `language ${index + 1}`}`}
                title={languages.length <= 1 ? "At least one language is required" : "Remove language"}
              >
                <Trash2 size={18} aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
      <datalist id={listId}>
        {COMMON_LANGUAGES.map((language) => <option key={language} value={language} />)}
      </datalist>
      <button
        className="profile-language-add"
        type="button"
        onClick={(event) => openEditor("new", event.currentTarget)}
        disabled={languages.length >= MAX_PROFILE_LANGUAGES}
      >
        <Plus size={17} aria-hidden="true" />
        Add another language
      </button>
      <p className={error ? "field-error" : "field-note"} role={error ? "alert" : undefined}>
        {error ?? `Add up to ${MAX_PROFILE_LANGUAGES} languages you can comfortably use.`}
      </p>

      {editorTarget !== null && createPortal(
        <div
          className="app-select-sheet-root language-editor-sheet-root"
          role="presentation"
          onClick={closeEditor}
        >
          <div
            className="app-select-sheet language-editor-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="app-select-sheet-header">
              <strong id={titleId}>
                {editorTarget === "new" ? "Add a language" : "Edit language"}
              </strong>
              <button
                className="icon-button app-select-sheet-close"
                type="button"
                onClick={closeEditor}
                aria-label="Close language editor"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="language-editor-sheet-body">
              <label className="language-editor-name">
                <span>Language</span>
                <input
                  ref={inputRef}
                  value={draft.language}
                  onChange={(event) => {
                    setDraft((current) => ({
                      ...current,
                      language: event.target.value,
                    }));
                    setDraftError(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      saveLanguage();
                    }
                  }}
                  list={listId}
                  maxLength={50}
                  autoComplete="off"
                  inputMode="text"
                  placeholder="e.g. English"
                  aria-invalid={!!draftError}
                  aria-describedby={draftError ? `${titleId}-error` : undefined}
                />
              </label>
              {draftError && (
                <p className="field-error" id={`${titleId}-error`} role="alert">
                  {draftError}
                </p>
              )}
              <fieldset className="language-editor-level">
                <legend>Proficiency</legend>
                <div className="language-editor-level-options">
                  {LANGUAGE_LEVELS.map((option) => {
                    const selected = modernLanguageLevel(draft.level) === option.value;
                    return (
                      <button
                        className={`language-editor-level-option${selected ? " selected" : ""}`}
                        type="button"
                        key={option.value}
                        aria-pressed={selected}
                        onClick={() => setDraft((current) => ({
                          ...current,
                          level: option.value,
                        }))}
                      >
                        <span>
                          <strong>{option.label}</strong>
                          <small>{LANGUAGE_LEVEL_DESCRIPTIONS[option.value]}</small>
                        </span>
                        {selected && <Check size={20} aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
              <button
                className="button primary language-editor-save"
                type="button"
                onClick={saveLanguage}
              >
                {editorTarget === "new" ? "Add language" : "Save changes"}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
